import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Palette, Check, Eye, Moon, Sun, Upload, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useTheme, DESIGN_TEMPLATES } from '@/contexts/ThemeContext';

const ThemePreviewCard = ({ template, isSelected, onSelect }) => {
  const { colors, mode, name, description } = template;
  
  return (
    <div 
      className={`relative rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer ${
        isSelected ? 'ring-4 scale-[1.02]' : 'hover:scale-[1.01]'
      }`}
      style={{
        backgroundColor: colors.background,
        ringColor: isSelected ? colors.primary : undefined,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
      }}
      onClick={() => onSelect(template)}
    >
      {isSelected && (
        <div className="absolute top-3 left-3 w-7 h-7 rounded-full flex items-center justify-center z-10"
          style={{ backgroundColor: colors.primary }}>
          <Check className="w-4 h-4 text-white" />
        </div>
      )}

      <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
        style={{
          backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
          color: colors.text
        }}>
        {mode === 'dark' ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
        {mode === 'dark' ? 'Dark' : 'Light'}
      </div>
      
      {/* Mini Header Preview */}
      <div className="p-4" style={{ backgroundColor: colors.header || colors.primary }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>
            Y
          </div>
          <div className="text-white">
            <div className="font-bold text-sm">YAS Connect</div>
            <div className="text-xs opacity-80">Preview</div>
          </div>
        </div>
      </div>
      
      {/* Mini Content Preview */}
      <div className="p-4 space-y-3">
        <div className="flex gap-2">
          <div className="flex-1 p-3 rounded-lg text-center"
            style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <div className="text-lg font-bold" style={{ color: colors.primary }}>125</div>
            <div className="text-xs" style={{ color: colors.text_muted }}>Points</div>
          </div>
          <div className="flex-1 p-3 rounded-lg text-center"
            style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <div className="text-lg font-bold" style={{ color: colors.secondary }}>7</div>
            <div className="text-xs" style={{ color: colors.text_muted }}>Streak</div>
          </div>
        </div>
        
        <button className="w-full py-2 rounded-lg font-semibold text-sm"
          style={{ backgroundColor: colors.primary, color: colors.text_on_primary }}>
          Primary Action
        </button>
        <button className="w-full py-2 rounded-lg font-semibold text-sm"
          style={{ backgroundColor: colors.secondary, color: '#fff' }}>
          Secondary
        </button>
      </div>
      
      {/* Theme Info */}
      <div className="p-4 border-t" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <h3 className="font-bold text-sm mb-1" style={{ color: colors.text }}>{name}</h3>
        <p className="text-xs" style={{ color: colors.text_muted }}>{description}</p>
        <div className="flex gap-1 mt-3">
          <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.primary }} />
          <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.secondary }} />
          <div className="w-5 h-5 rounded-full" style={{ backgroundColor: colors.accent }} />
          <div className="w-5 h-5 rounded-full border" style={{ backgroundColor: colors.background, borderColor: colors.border }} />
        </div>
      </div>
    </div>
  );
};

const ThemeConfigPage = ({ user }) => {
  const navigate = useNavigate();
  const { currentTemplate, switchTemplate, colors } = useTheme();
  const [saving, setSaving] = useState(false);
  
  const templates = Object.values(DESIGN_TEMPLATES);

  const handleSelect = async (template) => {
    switchTemplate(template.id);
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/theme/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ design_template: template.id })
      });
      toast.success(`Theme changed to "${template.name}"`);
    } catch (error) {
      console.error('Error saving theme:', error);
      toast.info('Theme applied locally. Save to server may have failed.');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="sticky top-0 z-10 border-b" style={{ backgroundColor: colors.surface, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)}
                className="p-2 rounded-lg transition-colors hover:opacity-80"
                style={{ color: colors.text }}>
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <Palette className="w-5 h-5" style={{ color: colors.primary }} />
                  <h1 className="text-xl font-bold" style={{ color: colors.text, fontFamily: 'var(--font-heading)' }}>
                    Design Templates
                  </h1>
                </div>
                <p className="text-sm" style={{ color: colors.text_muted }}>
                  Choose the look and feel for your entire program
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Current Theme Indicator */}
        <div className="mb-6 p-4 rounded-xl flex items-center gap-3"
          style={{ backgroundColor: `${colors.primary}15`, border: `1px solid ${colors.primary}30` }}>
          <Check className="w-5 h-5" style={{ color: colors.primary }} />
          <span style={{ color: colors.text }}>
            <strong>Current theme:</strong> {DESIGN_TEMPLATES[currentTemplate]?.name || currentTemplate}
          </span>
        </div>

        {/* Template Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {templates.map((template) => (
            <ThemePreviewCard
              key={template.id}
              template={template}
              isSelected={currentTemplate === template.id}
              onSelect={handleSelect}
            />
          ))}
        </div>
        
        {/* Description Grid */}
        <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <h3 className="font-bold mb-4" style={{ color: colors.text, fontFamily: 'var(--font-heading)' }}>
            About These Templates
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <strong style={{ color: '#1B2D4F' }}>Cash App Clean</strong>
              <p style={{ color: colors.text_muted }}>Ultra-minimal, professional. Great for workforce and career programs.</p>
            </div>
            <div>
              <strong style={{ color: '#58CC02' }}>Duo Energy</strong>
              <p style={{ color: colors.text_muted }}>Gamified and fun. Perfect for youth engagement and skill-building.</p>
            </div>
            <div>
              <strong style={{ color: '#2D9EA3' }}>Spotify Nights</strong>
              <p style={{ color: colors.text_muted }}>Immersive dark mode. Ideal for creative and leadership programs.</p>
            </div>
            <div>
              <strong style={{ color: '#FE2C55' }}>TikTok Feed</strong>
              <p style={{ color: colors.text_muted }}>Bold and social. Great for social-driven youth development.</p>
            </div>
            <div>
              <strong style={{ color: '#1B2D4F' }}>Smooth Vibes</strong>
              <p style={{ color: colors.text_muted }}>Warm and friendly. The original YAS Connect look. Versatile for any program.</p>
            </div>
          </div>
        </div>
      </div>

      {saving && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-xl p-6 text-center" style={{ backgroundColor: colors.surface }}>
            <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full mx-auto mb-3"
              style={{ borderColor: colors.primary }} />
            <p style={{ color: colors.text }}>Applying theme...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeConfigPage;
